import { RateTable, TimekeepingEntry } from "../common/types"
import { CachedLaborRate, LaborRateMatchContextParams } from "./types"
import { getObjectId } from "../common/ag-grid-ts-utils"
import { tEntitySelection } from "../cached-data/types"

/**
 * Extract labor rates from rate tables and return a normalized object keyed by labor rate ID.
 * Each labor rate is enriched with its parent rate table's id and name.
 *
 * @param rateTables - Object of rate tables keyed by ID
 * @returns Object of labor rates keyed by labor rate ID
 */
export const extractLaborRatesFromRateTables = (rateTables: { [key: number]: RateTable }): { [key: number]: CachedLaborRate } => {
    const laborRates: { [key: number]: CachedLaborRate } = {}

    Object.values(rateTables).forEach(rateTable => {
        if (rateTable.labor_rates) {
            rateTable.labor_rates.forEach(laborRate => {
                laborRates[laborRate.id] = {
                    ...laborRate,
                    version: rateTable.version,
                    rate_table: {
                        id: rateTable.id,
                        name: rateTable.name,
                    },
                }
            })
        }
    })

    return laborRates
}

/**
 * Extract the parameters needed to build a labor rate match context from a timekeeping entity.
 * Return primitive values that can be used to track when matching needs to be re-run.
 *
 * @param tkEntity - The timekeeping entry row data with employee, project, cost_code, date fields
 * @param referenceableData - Reference data containing cost codes to look up project IDs
 * @returns Parameters object with employeeId, projectId, costCodeId,
 *          modifiersString (JSON-stringified modifier IDs object), and entryDate
 */
export const buildLaborRateMatchContextParams = (
    tkEntity: Pick<TimekeepingEntry, "cost_code" | "employee" | "project" | "date" | "modifier_active">,
    referenceableData: tEntitySelection
): LaborRateMatchContextParams => {
    const costCodeId = tkEntity.cost_code ? getObjectId(tkEntity.cost_code) : null
    const employeeId = tkEntity.employee ? getObjectId(tkEntity.employee) : null
    const projectId = tkEntity.project ? getObjectId(tkEntity.project) : costCodeId ? referenceableData.costCodes[costCodeId]?.project?.id ?? null : null

    // Convert modifier_active object to just a record of modifier slug to ID, sort it, stringify it.
    type ModifiersObjectEntry = [string, NonNullable<TimekeepingEntry["modifier_active"]>[string]]
    const modifiersString = tkEntity.modifier_active
        ? JSON.stringify(
              Object.fromEntries(
                  Object.entries(tkEntity.modifier_active)
                      .map(([key, value]: ModifiersObjectEntry) => [key, getObjectId(value)])
                      .sort(([keyA], [keyB]) => (keyA as string).localeCompare(keyB as string))
              )
          )
        : "{}"

    return {
        costCodeId,
        employeeId,
        projectId,
        modifiersString,
        entryDate: tkEntity.date,
    }
}
